import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserProfile, selectTenant, setTenantData } from '../redux/userSlice';
import { CircularProgress, FormControl, InputLabel, Select, MenuItem, Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import config from '../utils/Constants';

const getTenantSlug = (tenant) => {
  if (tenant.slug) return tenant.slug;
  try {
    return new URL(tenant.api_base_url).hostname.split('.')[0];
  } catch {
    return null;
  }
};

const Profile = ({ tenantId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { profile, loading, error, selectedTenant, detailedLoading } = useSelector((state) => state.user);

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  // Auto-select tenant from URL param once profile is loaded.
  // Also re-selects if the URL tenantId doesn't match the currently selected tenant
  // (e.g. when navigating directly to a different tenant's URL).
  const selectTenantAndSetData = (tenant) => {
    const slug = getTenantSlug(tenant);
    dispatch(selectTenant({ ...tenant, id: slug }));
    dispatch(setTenantData({
      items: [{
        name: "Admissions",
        list_data_url: `${config.getTenantApiBase(tenant.api_base_url)}/api/v1/client/admissions/`,
      }],
    }));
  };

  useEffect(() => {
    if (!tenantId || !profile) return;
    const alreadySelected = selectedTenant && String(selectedTenant.id) === String(tenantId);
    if (!alreadySelected) {
      const tenant = profile.tenants?.find((t) => getTenantSlug(t) === tenantId);
      if (tenant) selectTenantAndSetData(tenant);
    }
  }, [tenantId, profile, selectedTenant, dispatch]);

  const handleTenantSelection = (event) => {
    const slug = event.target.value;
    const tenant = profile.tenants.find(t => getTenantSlug(t) === slug);
    if (tenant) {
      navigate(`/dashboard/${slug}`, { replace: true });
      selectTenantAndSetData(tenant);
    }
  };

  // Tenant names aren't unique on the backend, so disambiguate duplicates with the slug.
  const nameCounts = (profile?.tenants ?? []).reduce((acc, t) => {
    acc[t.name] = (acc[t.name] ?? 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Typography color="error">Error: {error}</Typography>;

  return (
    <Box>
      {profile && profile.tenants && profile.tenants.length > 0 && (
        <Box>
          <FormControl variant="outlined" style={{ marginTop: '5px', marginRight:"20px", marginBottom:"5px", backgroundColor:"#fff", minWidth: "300px", borderRadius:"20px" }}>
            <InputLabel id="tenant-select-label">Select Tenant</InputLabel>
            <Select
              labelId="tenant-select-label"
              value={selectedTenant?.id ?? ''}
              onChange={handleTenantSelection}
              label="Select Tenant"
              disabled={detailedLoading}
              IconComponent={detailedLoading ? () => <CircularProgress size={20} sx={{ mr: 1 }} /> : undefined}
            >
              {profile.tenants.map((tenant) => {
                const slug = getTenantSlug(tenant);
                if (!slug) return null;
                return (
                  <MenuItem key={slug} value={slug}>
                    {nameCounts[tenant.name] > 1 ? `${tenant.name} (${slug})` : tenant.name}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </Box>
      )}
    </Box>
  );
};

export default Profile;